import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { API_URLS } from '../../config/api';
import { CrawlSummary } from '../../api/types';
import { slugify } from '../../utils/urlUtils';
import Markdown from '../../components/Markdown';
import JoinPanel from '../../components/JoinPanel';
import '../../pages/PageStyles.css';
import './Crawls.css';

const Crawls: React.FC = () => {
  const [crawls, setCrawls] = useState<CrawlSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchCrawls = async () => {
      try {
        const response = await fetch(API_URLS.CRAWLS());
        if (!response.ok) {
          throw new Error(`Failed to fetch crawls: ${response.status}`);
        }
        const crawlList: CrawlSummary[] = await response.json();
        crawlList.sort((a, b) => a.name.localeCompare(b.name));
        setCrawls(crawlList);
        setError(null);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to fetch crawls');
      } finally {
        setLoading(false);
      }
    };
    fetchCrawls();
  }, []);

  if (loading) {
    return (
      <div className="page-container">
        <div className="page-content">
          <h1 className="page-title" data-testid="page-title">
            Crawls
          </h1>
          <div className="content-section">
            <p>Loading crawls…</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="page-container">
        <div className="page-content">
          <h1 className="page-title" data-testid="page-title">
            Crawls
          </h1>
          <div className="content-section">
            <p>Crawls couldn't be loaded. Check your signal, then try again.</p>
            <button onClick={() => window.location.reload()} className="retry-button">
              Try again
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="page-container">
      <div className="page-content">
        <h1 className="page-title" data-testid="page-title">
          Crawls
        </h1>

        <div className="content-section">
          <div className="crawl-index" data-testid="crawl-index">
            {crawls.map(crawl => (
              <Link
                key={crawl.id}
                to={`/crawls/${slugify(crawl.name)}`}
                className="crawl-index-card"
                data-testid="crawl-index-card"
              >
                <h3 className="crawl-index-name" data-testid="crawl-index-name">
                  {crawl.name}
                </h3>
                <Markdown className="crawl-index-description" disallowLinks>
                  {crawl.description}
                </Markdown>
                <div className="crawl-index-pills">
                  <span className="crawl-index-pill">{crawl.numOfPubs} pubs</span>
                  {crawl.averageDurationHours > 0 && (
                    <span className="crawl-index-pill">~{crawl.averageDurationHours}h</span>
                  )}
                </div>
              </Link>
            ))}
          </div>
        </div>

        <div className="content-section">
          <JoinPanel />
        </div>
      </div>
    </div>
  );
};

export default Crawls;
